import { FC, Fragment } from 'react';
import { StyleSheet } from '@react-pdf/renderer';
import Typography from '../Pdf/components/Typography';
import Column from '../Pdf/components/Column';
import theme from '../Pdf/components/theme';
import getSectionNumbers from './helpers/getSectionNumbers';
import PeriodEndReportPage from './components/pdf/PeriodEndReportPage';
import TableOfContentsEntry from './components/pdf/TableOfContentsEntry';
import Divider from './components/pdf/Divider';
import { PeriodEndReportPageProps, PeriodEndReportPdfLayout } from './types';

const styles = StyleSheet.create({
  title: {
    padding: theme.spacing(
      0,
      theme.spacingSizes.sm,
      theme.spacingSizes.md,
      theme.spacingSizes.sm,
    ),
  },
});

type Entry = {
  number: number;
  label: string;
  start: number;
  end: number;
  subEntries?: Array<Entry>;
};

type Props = PeriodEndReportPageProps & {
  layout: PeriodEndReportPdfLayout;
  hasCommentary: boolean;
  hasPortfolioUpdate: boolean;
};

const TableOfContents: FC<Props> = ({
  layout,
  hasCommentary,
  hasPortfolioUpdate,
  ...rest
}) => {
  const { financialSectionNumber, disclaimerSectionNumber } = getSectionNumbers(
    hasCommentary,
    hasPortfolioUpdate,
  );

  const entries: Array<Entry> = [
    {
      number: 1,
      label: 'Fund overview',
      start: layout.fundOverview,
      end: layout.fundOverview,
    },
  ];

  if (layout.fundManagerCommentary) {
    entries.push({
      number: 2,
      label: 'Fund manager commentary',
      start: layout.fundManagerCommentary.start,
      end: layout.fundManagerCommentary.end,
    });
  }

  if (layout.portfolioUpdate) {
    entries.push({
      number: hasCommentary ? 3 : 2,
      label: 'Portfolio updates',
      start: layout.portfolioUpdate.start,
      end: layout.portfolioUpdate.end,
    });
  }

  entries.push(
    {
      number: financialSectionNumber,
      label: 'Financial statements',
      start: layout.partThreeCover,
      end: layout.partThreeCover,
      subEntries: [
        {
          number: 1,
          label: 'Income statement',
          start: layout.incomeStatement,
          end: layout.incomeStatement,
        },
        {
          number: 2,
          label: 'Balance sheet',
          start: layout.balanceSheet,
          end: layout.balanceSheet,
        },
        {
          number: 3,
          label: 'Cash flow statement',
          start: layout.cashFlowStatement,
          end: layout.cashFlowStatement,
        },
        {
          number: 4,
          label: 'Schedule of investments',
          start: layout.scheduleOfInvestments,
          end: layout.scheduleOfInvestments,
        },
        {
          number: 5,
          label: 'PCAP',
          start: layout.pcap,
          end: layout.pcap,
        },
      ],
    },
    {
      number: disclaimerSectionNumber,
      label: 'Disclaimer',
      start: layout.disclaimer,
      end: layout.disclaimer,
    },
  );

  return (
    <PeriodEndReportPage {...rest}>
      <Typography variant="h5" style={styles.title}>
        Table of contents
      </Typography>
      <Column spacing="xxs">
        {entries.map((entry, index) => {
          const isNotLast = index !== entries.length - 1;
          const isFinancialStatementsEntry =
            entry.label === 'Financial statements';

          return (
            <Fragment key={entry.label}>
              <TableOfContentsEntry
                number={`0${entry.number}`}
                label={entry.label}
                pageStart={
                  isFinancialStatementsEntry
                    ? entry.subEntries?.at(0)?.start ?? entry.start
                    : entry.start
                }
                pageEnd={
                  isFinancialStatementsEntry
                    ? entry.subEntries?.at(-1)?.end ?? entry.end
                    : entry.end
                }
              />
              {entry.subEntries
                ? entry.subEntries.map((subEntry) => (
                    <TableOfContentsEntry
                      key={subEntry.label}
                      number={`${entry.number}.${subEntry.number}`}
                      label={subEntry.label}
                      pageStart={subEntry.start}
                      pageEnd={subEntry.end}
                      subEntry
                    />
                  ))
                : null}
              {isNotLast ? <Divider /> : null}
            </Fragment>
          );
        })}
      </Column>
    </PeriodEndReportPage>
  );
};

export default TableOfContents;
